import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { InsightCategory } from '@sprintpulse/shared';

const STATUS_STYLES: Record<InsightCategory['status'], { icon: string; color: string; bg: string; border: string }> = {
  positive:   { icon: '↑', color: 'var(--green)',  bg: 'var(--green-dim)',  border: 'var(--green)' },
  neutral:    { icon: '→', color: 'var(--text-2)', bg: 'var(--bg-panel)',   border: 'var(--border)' },
  concerning: { icon: '↓', color: 'var(--red)',    bg: 'var(--red-dim)',    border: 'var(--red)' },
};

const CATEGORY_ORDER: InsightCategory['category'][] = [
  'overall', 'sprint_health', 'quality', 'flow', 'capacity', 'actionable_items',
];

function ActionableCard({ category }: { category: InsightCategory }) {
  return (
    <div
      className="rounded-lg p-4 border sm:col-span-2"
      style={{
        background: 'var(--accent-dim)',
        borderColor: 'var(--accent)',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          ✓
        </div>
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
          {category.title}
        </h4>
      </div>
      <p className="text-xs mb-3 leading-relaxed" style={{ color: 'var(--text-2)' }}>
        {category.summary}
      </p>
      <ol className="space-y-2">
        {category.highlights.map((h, i) => (
          <li key={i} className="flex gap-3 text-xs">
            <span
              className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: 'var(--accent)', color: '#fff', marginTop: '1px' }}
            >
              {i + 1}
            </span>
            <span style={{ color: 'var(--text-1)' }}>{h}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function InsightCard({ category }: { category: InsightCategory }) {
  if (category.category === 'actionable_items') {
    return <ActionableCard category={category} />;
  }

  const style = STATUS_STYLES[category.status];
  return (
    <div
      className="rounded-lg p-4 border"
      style={{ background: style.bg, borderColor: style.border }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0"
          style={{ background: style.color, color: '#fff' }}
        >
          {style.icon}
        </span>
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
          {category.title}
        </h4>
      </div>
      <p className="text-xs mb-3 leading-relaxed" style={{ color: 'var(--text-2)' }}>
        {category.summary}
      </p>
      <ul className="space-y-1">
        {category.highlights.map((h, i) => (
          <li key={i} className="flex gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
            <span className="shrink-0 mt-0.5" style={{ color: style.color }}>•</span>
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function InsightsPanel({ teamId, sprintId }: { teamId: string; sprintId: string }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['insights', teamId, sprintId],
    queryFn: () => api.getInsights(teamId, sprintId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Sort: overall first, then sprint_health/quality/flow/capacity, actionable_items last
  const sorted = data?.categories.slice().sort(
    (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
  );

  // Separate overall + metric cards from actionable items
  const overallAndMetrics = sorted?.filter((c) => (c.category as string) !== 'actionable_items');
  const actionable = sorted?.find((c) => (c.category as string) === 'actionable_items');

  return (
    <div
      className="rounded-lg border mt-6"
      style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
    >
      {/* Header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-6 h-6 rounded flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 2L10.5 7.5L16 9L10.5 10.5L9 16L7.5 10.5L2 9L7.5 7.5Z"/>
              <path d="M18 12L19 15L22 16L19 17L18 20L17 17L14 16L17 15Z"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
              Sprint Insights
            </p>
            <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-3)' }}>
              {open && data ? (
                <>
                  <span
                    className="font-medium px-1.5 py-0.5 rounded text-xs"
                    style={
                      data.sprintState === 'active'
                        ? { background: 'var(--amber-dim, #fef3c7)', color: 'var(--amber, #b45309)', fontSize: '10px', letterSpacing: '0.04em' }
                        : { background: 'var(--green-dim)', color: 'var(--green)', fontSize: '10px', letterSpacing: '0.04em' }
                    }
                  >
                    {data.sprintState === 'active'
                      ? `PARTIAL${data.sprintProgressPct !== null ? ` · ${data.sprintProgressPct}%` : ''}`
                      : 'FINAL'}
                  </span>
                  <span>Generated {new Date(data.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                </>
              ) : 'Click to generate AI analysis'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {open && data && (
            <button
              onClick={(e) => { e.stopPropagation(); refetch(); }}
              className="text-xs px-2 py-1 rounded border"
              style={{ color: 'var(--text-3)', borderColor: 'var(--border)' }}
              title="Regenerate insights"
            >
              ↻ Refresh
            </button>
          )}
          <span
            className="text-xs font-medium"
            style={{
              color: 'var(--accent)',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block',
              transition: 'transform 0.2s',
            }}
          >
            ▾
          </span>
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: 'var(--border)' }}>
          {(isLoading || isFetching) && (
            <div className="flex items-center gap-3 py-8 justify-center">
              <div
                className="w-4 h-4 rounded-full border-2 animate-spin"
                style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
              />
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>
                Analysing sprint data…
              </span>
            </div>
          )}

          {isError && !isFetching && (
            <div className="py-6 text-center">
              <p className="text-sm" style={{ color: 'var(--red)' }}>
                Failed to generate insights. Check backend logs for details.
              </p>
              <button
                onClick={() => refetch()}
                className="mt-3 text-xs px-3 py-1.5 rounded border"
                style={{ color: 'var(--text-2)', borderColor: 'var(--border)' }}
              >
                Try again
              </button>
            </div>
          )}

          {sorted && !isFetching && (
            <div className="pt-4 space-y-4">
              {/* Metric insight cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {overallAndMetrics?.map((cat) => (
                  <InsightCard key={cat.category} category={cat} />
                ))}
              </div>

              {/* Actionable items — full width, visually distinct */}
              {actionable && (
                <div className="grid grid-cols-1">
                  <InsightCard category={actionable} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}